import ExperimentSplashCtaTextExperiment from '@wetransfer/transfer/constant/experiments/splash-cta-text-experiment';
import ExperimentSplashPageDiscountLabel from '@wetransfer/transfer/constant/experiments/splash-page-discount-label';
import ExperimentSplashPageShowPremiumCard from '@wetransfer/transfer/constant/experiments/splash-page-show-premium-card';
import ExperimentSplashShowAllTiers from '@wetransfer/transfer/constant/experiments/splash-show-all-tiers-experiment';
import {
  FREE_ACCOUNT,
  PLUS_ACCOUNT,
} from '@wetransfer/transfer/constant/subscriptions';
import Helper from '@wetransfer/transfer/helper';
import { createPricingRoute } from '@wetransfer/transfer/helper/routeFactory';
import { getProTransferLimit } from '@wetransfer/transfer/helper/subscriptions';
import I18n from '@wetransfer/transfer/modules/i18n';

const controlFreeCta = {
  text: I18n.t('splash.plan.free_account.cta'),
  href: '/sign-up?tier=free&trk=WT202005_splashpage_anonym_to_fa',
};

const getProButtonCtaURL = () => {
  const proCTAButtonURL =
    '/sign-up?tier=plus&trk=WT202005_splashpage_anonym_to_pro';

  if (ExperimentSplashPageDiscountLabel.isShowSplashpageDiscountLabel()) {
    const discountData = Helper.getCurrentExperimentData(
      ExperimentSplashPageDiscountLabel.payload
    );

    return `${proCTAButtonURL}?coupon=${discountData.couponCode}`;
  }

  return proCTAButtonURL;
};

export const freeFeatures = (): Features => [
  {
    usp: `Send up to <strong>${FREE_ACCOUNT.TRANSFER_LIMIT}</strong>`,
    check: true,
  },
  {
    usp: 'No need to verify transfers',
    check: true,
  },
  {
    usp: 'The basics of Paste® and Collect',
    check: true,
  },
];

export const proFeatures = (): Features => [
  {
    usp: `Send and receive up to <strong>${getProTransferLimit()}</strong>`,
    check: true,
  },
  { usp: `${PLUS_ACCOUNT.STORAGE_LIMIT} storage`, check: true },
  {
    usp: 'Decide when transfers expire',
    check: true,
  },
  {
    usp: 'Your own WeTransfer page',
    check: true,
  },
  {
    usp: 'The best of Paste® and Collect',
    check: true,
  },
];

export const bundleFreeFeatures = (): Features => [
  {
    usp: I18n.t('page.splashpage.plan.free.feature_1', {
      transfer_limit: FREE_ACCOUNT.TRANSFER_LIMIT,
    }),
    check: true,
  },
  {
    usp: I18n.t('page.splashpage.plan.free.feature_2'),
    check: true,
  },
];

export const bundleProFeatures = (): Features => [
  {
    usp: I18n.t('page.splashpage.plan.pro.feature_1', {
      transfer_limit: getProTransferLimit(),
    }),
    check: true,
  },
  {
    usp: I18n.t('page.splashpage.plan.pro.feature_5'),
    check: true,
  },
  {
    usp: I18n.t('page.splashpage.plan.pro.feature_2'),
    check: true,
  },
];

export const premiumFeatures = (): Features => [
  {
    usp: 'No size limit on transfers',
    check: true,
  },
  { usp: 'Unlimited storage', check: true },
  {
    usp: 'Unlimited portals and reviews',
    check: true,
  },
];

export const receiverFreeFeatures = (): Features => [
  {
    usp: I18n.t('page.splashpage.reciever.free.feature_1'),
    check: true,
  },
  {
    usp: I18n.t('page.splashpage.reciever.free.feature_2'),
    check: true,
  },
];

export const receiverProFeatures = (): Features => [
  {
    usp: I18n.t('page.splashpage.reciever.pro.feature_1', {
      transfer_size: getProTransferLimit(),
    }),
    check: true,
  },
  {
    usp: I18n.t('page.splashpage.reciever.pro.feature_2', {
      storage_size: PLUS_ACCOUNT.STORAGE_LIMIT,
    }),
    check: true,
  },
  {
    usp: I18n.t('page.splashpage.reciever.pro.feature_3'),
    check: true,
  },
];

export const bundlePremiumFeatures = (): Features => [
  {
    usp: I18n.t('page.pricing_panel.plans.premium.usp.feature_1'),
    check: true,
  },
  {
    usp: I18n.t('page.pricing_panel.plans.premium.usp.feature_2'),
    check: true,
  },
  {
    usp: I18n.t('page.pricing_panel.plans.premium.usp.feature_3'),
    check: true,
  },
];

const receiverFreeCta = {
  text: I18n.t('page.splashpage.reciever.free.cta'),
  href: '/sign-up?tier=free&trk=WT202005_splashpage_anonym_to_fa',
};

const receiverProCta = {
  text: I18n.t('page.splashpage.reciever.pro.cta'),
  href: createPricingRoute(),
};

const controlPremiumCta = {
  text: 'Get WeTransfer Premium',
  href: '/sign-up?tier=premium_2022&trk=WT202005_splashpage_anonym_to_premium',
};

type Features = { usp: string; check: boolean }[];

interface CTAProps {
  text: string;
  href: string;
}

interface ContentProps {
  title?: string;
  description?: string;
  features: Features;
  cta?: CTAProps;
}

interface ReceiverContentProps {
  title: string;
  description?: string;
  features: Features;
  cta?: typeof receiverFreeCta | typeof receiverProCta;
}

interface Props {
  title?: string;
  dismissCtaText?: string;
  freeContent: ContentProps;
  proContent: ContentProps;
  premiumContent: ContentProps;
}

interface ReceiverProps {
  title?: string;
  dismissCtaText?: string;
  freeContent: ReceiverContentProps;
  proContent: ReceiverContentProps;
}

export const controlProps = ({
  freeFeatures,
  proFeatures,
  premiumFeatures,
}: {
  freeFeatures: Features;
  proFeatures: Features;
  premiumFeatures: Features;
}): Props => {
  const controlProCta = {
    text: I18n.t('splash.plan.pro.cta'),
    href: getProButtonCtaURL(),
  };

  return {
    freeContent: {
      features: freeFeatures,
      cta: controlFreeCta,
      title: 'WeTransfer account',
      description: I18n.t('page.pricing_panel.plans.free.subtitle'),
    },
    proContent: {
      features: proFeatures,
      cta: controlProCta,
      title: I18n.t('page.pricing_panel.plans.pro.subtitle'),
    },
    premiumContent: {
      features: premiumFeatures,
      cta: controlPremiumCta,
      title: I18n.t('page.pricing_panel.plans.premium.title'),
      description: I18n.t('page.pricing_panel.plans.premium.subtitle'),
    },
  };
};

export const receiverProps = ({
  freeFeatures,
  proFeatures,
}: {
  freeFeatures: Features;
  proFeatures: Features;
}): ReceiverProps => ({
  freeContent: {
    features: freeFeatures,
    cta: receiverFreeCta,
    title: I18n.t('page.splashpage.reciever.free.title'),
    description: I18n.t('page.splashpage.reciever.free.descripttion'),
  },
  proContent: {
    features: proFeatures,
    cta: receiverProCta,
    title: I18n.t('page.splashpage.reciever.pro.title'),
    description: I18n.t('page.splashpage.reciever.pro.descripttion'),
  },
});

export const variantCProps = ({
  freeContent,
  proContent,
  premiumContent,
  bundleFreeFeatures,
  bundleProFeatures,
  bundlePremiumFeatures,
}: {
  freeContent: ContentProps;
  proContent: ContentProps;
  premiumContent: ContentProps;
  bundleFreeFeatures: Features;
  bundleProFeatures: Features;
  bundlePremiumFeatures: Features;
}): Props => {
  const shouldUpdateCopy = ExperimentSplashCtaTextExperiment.isEnabled();

  const showPremiumCard = ExperimentSplashPageShowPremiumCard.isEnabled();

  const showAllTiersCard = ExperimentSplashShowAllTiers.isEnabled();

  if (shouldUpdateCopy) {
    proContent = {
      ...proContent,
      cta: {
        text: I18n.t('page.pro_classic.hero.cta_1'),
        href: createPricingRoute(),
      },
    };
  }

  if (showPremiumCard || showAllTiersCard) {
    if (shouldUpdateCopy) {
      premiumContent = {
        ...premiumContent,
        cta: {
          text: I18n.t('page.pro_classic.hero.cta_1'),
          href: createPricingRoute(),
        },
      };
    } else {
      premiumContent = {
        ...premiumContent,
        cta: controlPremiumCta,
      };
    }
  }

  return {
    title: I18n.t(`page.splashpage.bundle.title_new`),
    dismissCtaText: I18n.t('page.splashpage.cta'),
    freeContent: {
      ...freeContent,
      cta: {
        ...controlFreeCta,
        text: I18n.t('page.splashpage.free_panel.cta'),
      },
      features: bundleFreeFeatures,
      title: I18n.t('page.splashpage.plan.free.title'),
      description: I18n.t(`page.splashpage.plan.free.subtitle_new`),
    },
    proContent: {
      ...proContent,
      features: bundleProFeatures,
      title: I18n.t('page.splashpage.plan.pro.title'),
      description: I18n.t(`page.splashpage.plan.pro.subtitle_new`),
    },
    premiumContent: {
      ...premiumContent,
      features: bundlePremiumFeatures,
    },
  };
};
